import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MonitorHeart as MonitorHeartIcon } from '@mui/icons-material';
import { FacilityService } from '../../services/facilityService';
import { getBedsByRoom, getBedsByFacility } from '../../services/bedService';
import { updateEncounterLocation } from '../../services/encounterService';
import { useUserData } from '../../hooks/useUserData';

// Styled Components
const CompactSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const SectionHeaderWithIcon = ({ icon: Icon, children, ...props }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      fontWeight: 600,
      fontSize: '1rem',
      color: 'primary.main',
      padding: '8px 12px',
      borderBottom: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'grey.50',
      margin: 0,
    }}
    {...props}
  >
    <Icon sx={{ fontSize: 20 }} />
    {children}
  </Box>
);

const SectionContent = styled(Box)({
  padding: '8px 12px',
});

const LocationInformationPanel = React.memo(({
  bedInfo = {},
  encounterId,
  onLocationUpdate,
  disabled = false
}) => {
  const { defaultFacility } = useUserData();
  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state for updates
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedBed, setSelectedBed] = useState('');
  
  // Get current facility ID
  const facilityId = defaultFacility?.id;
  
  // Load all location data
  const loadLocationData = useCallback(async () => {
    if (!facilityId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Load departments
      const depts = await FacilityService.getFacilityDepartments(facilityId);
      setDepartments(depts);
      
      // Load rooms (all rooms for the facility)
      const rms = await FacilityService.getFacilityRooms(facilityId);
      setRooms(rms);
      
      // Load beds (all beds for the facility)
      const facilityBeds = await getBedsByFacility(facilityId);
      setBeds(facilityBeds);
      
    } catch (error) {
      console.error('Error loading location data:', error);
      setError('Failed to load location data');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);
  
  // Load departments, rooms, and beds when component mounts or facility changes
  useEffect(() => {
    if (facilityId) {
      loadLocationData();
    }
  }, [facilityId, loadLocationData]);
  
  // Set initial form values from bedInfo when it changes
  // Must wait for departments/rooms/beds to load before matching
  useEffect(() => {
    if (bedInfo && departments.length > 0) {
      // Get the raw value from bedInfo
      const deptRaw = bedInfo?.departmentId || bedInfo?.department || bedInfo?.departmentName || '';
      const roomRaw = bedInfo?.roomId || bedInfo?.room || bedInfo?.roomName || '';
      const bedRaw = bedInfo?.bedId || bedInfo?.bed || bedInfo?.bedName || '';
      
      // Try to match department by ID first, then by name
      let deptValue = '';
      if (deptRaw) {
        const matchedDept = departments.find(d => 
          d.id === deptRaw || 
          d.name === deptRaw || 
          String(d.id) === String(deptRaw)
        );
        deptValue = matchedDept?.id || '';
      }
      
      // Try to match room by ID first, then by name/code
      let roomValue = '';
      if (roomRaw && rooms.length > 0) {
        const matchedRoom = rooms.find(r => 
          r.id === roomRaw || 
          r.code === roomRaw || 
          r.name === roomRaw ||
          String(r.id) === String(roomRaw)
        );
        roomValue = matchedRoom?.id || matchedRoom?.code || '';
      }
      
      // Try to match bed by ID first, then by name/code
      let bedValue = '';
      if (bedRaw && beds.length > 0) {
        const matchedBed = beds.find(b => 
          b.id === bedRaw || 
          b.code === bedRaw || 
          b.name === bedRaw ||
          String(b.id) === String(bedRaw)
        );
        bedValue = matchedBed?.id || matchedBed?.code || '';
      }
      
      setSelectedDepartment(deptValue);
      setSelectedRoom(roomValue);
      setSelectedBed(bedValue);
    }
  }, [bedInfo, departments, rooms, beds]);
  
  // Load rooms when department changes
  const handleDepartmentChange = useCallback(async (departmentId) => {
    setSelectedDepartment(departmentId);
    setSelectedRoom(''); // Reset room selection
    setSelectedBed(''); // Reset bed selection
    
    if (departmentId && facilityId) {
      try {
        const deptRooms = await FacilityService.getFacilityRooms(facilityId, departmentId);
        setRooms(deptRooms);
      } catch (error) {
        console.error('Error loading rooms for department:', error);
      }
    } else {
      // Load all rooms if no department selected
      loadLocationData();
    }
  }, [facilityId, loadLocationData]);
  
  // Load beds when room changes
  const handleRoomChange = useCallback(async (roomId) => {
    setSelectedRoom(roomId);
    setSelectedBed(''); // Reset bed selection
    
    if (roomId) {
      try {
        const roomBeds = await getBedsByRoom(roomId, facilityId);
        setBeds(roomBeds);
      } catch (error) {
        console.error('Error loading beds for room:', error);
      }
    } else {
      // Load all beds if no room selected
      if (facilityId) {
        try {
          const facilityBeds = await getBedsByFacility(facilityId);
          setBeds(facilityBeds);
        } catch (error) {
          console.error('Error loading facility beds:', error);
        }
      }
    }
  }, [facilityId]);
  
  // Handle bed change
  const handleBedChange = useCallback((bedId) => {
    setSelectedBed(bedId);
  }, []);
  
  // Update location information
  const handleUpdateLocation = useCallback(async () => {
    if (!encounterId || !facilityId) {
      setError('Missing encounter ID or facility ID');
      return;
    }
    
    setUpdating(true);
    setError(null);
    
    try {
      const locationData = {
        departmentId: selectedDepartment,
        roomId: selectedRoom,
        bedId: selectedBed,
        facilityId
      };
      
      // Update encounter location
      await updateEncounterLocation(encounterId, locationData);
      
      // Call parent callback if provided
      if (onLocationUpdate) {
        onLocationUpdate(locationData);
      }
      
      setSuccess(true);
      
      // Reset form after successful update
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error updating location:', error);
      setError('Failed to update location information');
    } finally {
      setUpdating(false);
    }
  }, [encounterId, facilityId, selectedDepartment, selectedRoom, selectedBed, onLocationUpdate]);
  

  
  // Check if form has changes
  const hasChanges = useCallback(() => {
    // Get raw values from bedInfo
    const deptRaw = bedInfo?.departmentId || bedInfo?.department || bedInfo?.departmentName || '';
    const roomRaw = bedInfo?.roomId || bedInfo?.room || bedInfo?.roomName || '';
    const bedRaw = bedInfo?.bedId || bedInfo?.bed || bedInfo?.bedName || '';
    
    // Match to actual IDs like we do in the useEffect
    let currentDept = '';
    if (deptRaw && departments.length > 0) {
      const matchedDept = departments.find(d => 
        d.id === deptRaw || d.name === deptRaw || String(d.id) === String(deptRaw)
      );
      currentDept = matchedDept?.id || '';
    }
    
    let currentRoom = '';
    if (roomRaw && rooms.length > 0) {
      const matchedRoom = rooms.find(r => 
        r.id === roomRaw || r.code === roomRaw || r.name === roomRaw || String(r.id) === String(roomRaw)
      );
      currentRoom = matchedRoom?.id || matchedRoom?.code || '';
    }
    
    let currentBed = '';
    if (bedRaw && beds.length > 0) {
      const matchedBed = beds.find(b => 
        b.id === bedRaw || b.code === bedRaw || b.name === bedRaw || String(b.id) === String(bedRaw)
      );
      currentBed = matchedBed?.id || matchedBed?.code || '';
    }
    
    return selectedDepartment !== currentDept || 
           selectedRoom !== currentRoom || 
           selectedBed !== currentBed;
  }, [bedInfo, departments, rooms, beds, selectedDepartment, selectedRoom, selectedBed]);
  
  if (loading) {
    return (
      <CompactSection>
        <SectionHeaderWithIcon icon={MonitorHeartIcon}>Location Information</SectionHeaderWithIcon>
        <SectionContent sx={{ py: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </SectionContent>
      </CompactSection>
    );
  }
  
  return (
    <>
      <CompactSection>
        <SectionHeaderWithIcon icon={MonitorHeartIcon}>Location Information</SectionHeaderWithIcon>
        <SectionContent sx={{ py: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' }}>Department</InputLabel>
                <Select
                  value={selectedDepartment}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  label="Department"
                  disabled={disabled}
                  sx={{
                    '& .MuiSelect-select': {
                      fontFamily: 'Roboto, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select Department</em>
                  </MenuItem>
                  {departments.map(dept => (
                    <MenuItem 
                      key={dept.id || dept.name} 
                      value={dept.id || dept.name}
                      sx={{
                        fontFamily: 'Roboto, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {dept.name || dept.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' }}>Room</InputLabel>
                <Select
                  value={selectedRoom}
                  onChange={(e) => handleRoomChange(e.target.value)}
                  label="Room"
                  disabled={disabled}
                  sx={{
                    '& .MuiSelect-select': {
                      fontFamily: 'Roboto, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select Room</em>
                  </MenuItem>
                  {rooms.map(room => (
                    <MenuItem 
                      key={room.id || room.code} 
                      value={room.id || room.code}
                      sx={{
                        fontFamily: 'Roboto, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {room.name || room.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 500, fontSize: '0.875rem' }}>Bed</InputLabel>
                <Select
                  value={selectedBed}
                  onChange={(e) => handleBedChange(e.target.value)}
                  label="Bed"
                  disabled={disabled}
                  sx={{
                    '& .MuiSelect-select': {
                      fontFamily: 'Roboto, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>No bed</em>
                  </MenuItem>
                  {beds.map(bed => (
                    <MenuItem 
                      key={bed.id || bed.code} 
                      value={bed.id || bed.code}
                      sx={{
                        fontFamily: 'Roboto, Arial, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {bed.name || bed.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Update Button */}
          {!disabled && encounterId && hasChanges() && (
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleUpdateLocation}
                disabled={updating}
                startIcon={updating ? <CircularProgress size={16} /> : null}
              >
                {updating ? 'Updating...' : 'Update Location'}
              </Button>
            </Box>
          )}
        </SectionContent>
      </CompactSection>
      
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
      >
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Location updated successfully
        </Alert>
      </Snackbar>
    </>
  );
});

export default LocationInformationPanel; 